<template>
  <div

    :class="{ 'is-home': isHome, 'is-single': banners.length == 1 }"
    v-if="canDisplay"
  >
    <div class="glide" :ref="`glide_${uid}`">
      <div class="glide__track" data-glide-el="track">
        <ul class="glide__slides c-carousel__slides" v-show="loaded">
          <!--@slot default slot for carousel items.  Should be li.glide__slide -->
          <li
            class="glide__slide"
            v-for="(displayBanner, index) in banners"
            :key="index"
            :data-glide-autoplay="displayBanner.duration"
          >
            <aside :style="holderBg">
              <div class="c-hero--multi__slide">
                <div class="c-hero--multi__content">
                  <h3 class="c-hero-slider__title">
                    {{ displayBanner.title }}
                  </h3>

                  <div class="c-hero-slider__buttons">
                    <base-button
                      outline
                      @click="handleClick(index)"
                      v-if="displayBanner.button_text"
                      >{{ displayBanner.button_text }}</base-button
                    >
                    <base-button
                      outline
                      @click="toggleVideo(index)"
                      primary="true"
                      icon-after="arrow"
                      v-show="bannerHasVideo(index)"
                      >Watch Video
                    </base-button>
                  </div>
                </div>

                <image-loader
                  v-if="displayBanner.file_id"
                  :image-id="displayBanner.file_id"
                  class="c-hero--multi__bg"
                />
                <image-loader
                  v-else
                  :image-id="displayBanner.file ? displayBanner.file.id : null"
                  class="c-hero--multi__bg"
                />
              </div>
            </aside>
          </li>
        </ul>
      </div>

      <div
        class="glide__bullets c-hero--multi__bullets"
        data-glide-el="controls[nav]"
        v-if="banners.length > 1"
      >
        <button
          class="glide__bullet"
          :data-glide-dir="`=${index}`"
          v-for="(banner, index) in banners"
          :key="index"
        ></button>
      </div>
      <div
        data-glide-el="controls"
        class="c-hero--multi__controls"
        v-if="banners.length > 1"
      >
        <button data-glide-dir="<" aria-labelledby="label--prev">
          <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M16 7a1 1 0 1 1 0 2V7ZM.293 8.707a1 1 0 0 1 0-1.414L6.657.929A1 1 0 0 1 8.07 2.343L2.414 8l5.657 5.657a1 1 0 1 1-1.414 1.414L.293 8.707ZM16 9H1V7h15v2Z"
              fill="#fff"
            />
          </svg>
          <span class="screen-reader-text" id="label--prev">Previous banner</span>
        </button>
        <button data-glide-dir=">" aria-labelledby="label--next">
          <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1 7a1 1 0 0 0 0 2V7Zm15.707 1.707a1 1 0 0 0 0-1.414L10.343.929A1 1 0 1 0 8.93 2.343L14.586 8l-5.657 5.657a1 1 0 0 0 1.414 1.414l6.364-6.364ZM1 9h15V7H1v2Z"
              fill="#fff"
            />
          </svg>
          <span class="screen-reader-text" id="label--next">Next banner</span>
        </button>
      </div>
    </div>
    <modal-video :video-link="videoLink" v-if="showVideo" @close="showVideo = false" />
  </div>
</template>

<script>
import Glide, {
  Autoplay,
  Swipe,
  Controls,
  Breakpoints,
  Images,
  Anchors,
} from '@glidejs/glide/dist/glide.modular.esm';
import ModalVideo from './ModalVideo.vue';
import { mapState } from 'vuex';
import ImageLoader from '@/components/elements/ImageLoader.vue';
import filesApi from '@/api/files';

export default {
  components: { ModalVideo, ImageLoader },
  name: 'HeroSliderMulti',
  props: {
    slider: {
      type: Object,
      default: () => {},
    },
    display: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    bannerHasVideo(index) {
      const displayBanner = this.banners[index];
      return (
        (displayBanner.video && displayBanner.video.link) ||
        (displayBanner.video_id && displayBanner.video_id.link) ||
        displayBanner.video_url
      );
    },
    async toggleVideo(index) {
      if (!this.bannerHasVideo(index)) return false;
      this.showVideo = !this.showVideo;
      const displayBanner = this.banners[index];
      if (displayBanner.video_url) {
        this.videoLink = displayBanner.video_url;
      } else {
        // need to get the video, put the link into the modal, display it
        const videoLink = displayBanner.video
          ? displayBanner.video.link
          : displayBanner.video_id.link;
        const data = await filesApi.readLink(videoLink);
        this.videoLink = URL.createObjectURL(data.data); // create a link rather then base64
      }
    },
    getVideoContent(data) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = function () {
          resolve(reader.result);
        };
      });
    },
    handleClick(index) {
      const displayBanner = this.banners[index];
      if (displayBanner.link_url) {
        return window.open(displayBanner.link_url);
      }

      // if the resource is a document or policy
      if (displayBanner.resource.current_version) {
        // eforms are different too
        if (displayBanner.resource.self.includes('eformmanagement')) {
          return this.$router.push({
            path: `/forms/${displayBanner.resource_obj.eform_url}`,
          });
        }
        // surveys are different too
        if (displayBanner.resource.self.includes('survey')) {
          return this.$router.push({
            path: `/survey/${displayBanner.resource.survey_url}`,
          });
        }
        const docId = displayBanner.resource.id;
        const docType = displayBanner.resource.self.includes('policy')
          ? 'policies'
          : 'forms';
        return this.$router.push({ path: `/documents/${docType}/${docId}` });
      }

      // if we are a file
      if (displayBanner.resource.file_hash) {
        this.$store
          .dispatch('files/read', displayBanner.resource.id)
          .then(resp => this.$store.dispatch('files/download', resp));

        return;
      }

      // content page
      if (displayBanner.resource.page_url) {
        // account for if url starts with a slash or not
        const url =
          displayBanner.resource.page_url.substr(0, 1) == '/'
            ? displayBanner.resource.page_url
            : `/${displayBanner.resource.page_url}`;
        return this.$router.push({
          path: `/content${url}`,
        });
      }

      this.$router.push({ path: `/${displayBanner.resource.self}` });
    },
    async initGlider() {
      try {
        // const banner = this.slider
        //   ? this.slider
        //   : await this.$store.dispatch("Banner/loadActiveSlider");
        if (this.banners.length > 1) {
          this.$nextTick(() => {
            const glide = new Glide(
              this.$refs[`glide_${this.uid}`],
              this.carouselSettings,
            );
            glide.on('swipe.end', () => {
              // this.updateButtons();
            });
            glide.on('build.after', () => {
              this.loaded = true;
            });
            glide.on('run.before', () => {
              if (this.$refs[`glide_${this.uid}`]) {
                this.$refs[`glide_${this.uid}`].classList.add('transition');
              }
            });
            glide.on('run.after', () => {
              if (this.$refs[`glide_${this.uid}`]) {
                this.$refs[`glide_${this.uid}`].classList.remove('transition');
              }
            });
            glide.mount({
              Autoplay,
              Swipe,
              Controls,
              Breakpoints,
              Images,
              Anchors,
            });

            this.glide = glide;
          });
        } else {
          this.loaded = true;
        }
      } catch (err) {
        console.log(
          '🚀 ~ file: HeroSliderMulti.vue ~ line 157 ~ mounted:function ~ err',
          err,
        );
      }
    },
  },
  async mounted() {
    await this.$store.dispatch('Banner/loadActiveSlider');
    // get the current slider
    this.initGlider();
  },
  computed: {
    ...mapState('Banner', ['currentBannerSet']),
    canDisplay() {
      return (!this.$route.meta.isAdmin && !this.$route.meta.hideBanner) || this.display;
    },
    glideRef() {
      return this.$refs[`glide_${this.uid}`];
    },
    banners() {
      return this.slider ? this.slider.banners : this.currentBannerSet.banners;
    },
    isHome() {
      return this.$route.name == 'Dashboard';
    },
  },
  data() {
    return {
      showVideo: false,
      showBanner: true,
      glide: {},
      loaded: false,
      uid: Math.random().toString(36).substring(7),
      carouselSettings: {
        type: 'slider', // switching to carousel seems to break the slider
        perView: 1,
        gap: 0,
        autoplay: 5000,
        hoverpause: true,
      },
      holderBg: { background: '#768692' },
      videoLink: '',
    };
  },
  watch: {
    canDisplay: {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {
        if (newValue) {
          this.initGlider();
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "node_modules/@glidejs/glide/src/assets/sass/glide.core";

.c-hero--multi {
  position: relative;
  height: 300px;
  width: 100%;

  &__slide,
  .glide {
    height: 300px;
  }


  @media (min-width: 768px) {
      height:212px;
      &__slide,
      .glide {
        height: 212px;
      }
  }
    @media (min-width: 1440px) {
      height:300px;
      &__slide,
      .glide {
        height: 300px;
      }
  }


  &.is-single {
    .glide__slides {
      display: block !important;
    }
  }

  &__bg {
    height: 100%;
    left: 0;
    object-fit: cover;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 0;
  }

  &__slide {
    position: relative;
  }

  &__content {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    position: relative;
    z-index: 1;
  }

  &__bullets {
    bottom: 20px;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);

    .glide__bullet {
      background: $colour-nhs-core-white;
      border: none;
      border-radius: 100%;
      box-sizing: border-box;
      cursor: pointer;
      height: 10px;
      padding: 0;
      box-shadow: 0 0 0 2px white;
      transition: background-color ease-in-out 0.2s;
      width: 10px;

      &:hover {
        background: $colour-nhs-med-grey;
      }

      &.glide__bullet {
        margin-left: 20px;
      }

      &--active {
        background: $colour-nhs-core-blue;
      }
      @media (min-width: 1440px) {
        width: 12px;
        height: 12px;
      }
    }
  }

  &__controls {
    display: flex;
    justify-content: space-between;
    padding: 0 2vw;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;

    button {
      background: $colour-nhs-core-blue;
      cursor: pointer;
      border: none;
      padding: 7px;

      &:hover {
        background: $colour-nhs-bright-blue;
      }
      @media (min-width: 1440px) {
        padding: 15px;
      }
    }


  }
}

.glide__slide--active {
  z-index: 1;
}

.c-hero-slider__buttons {
  position: absolute;
  bottom: 60px;
  left: 0;
  right: 0;
  width: 100%;
  text-align: center;
  .c-button {
    height: 38px;
    padding: 0.5rem 1rem;
    @media (min-width: 1440px) {
      height: 52px;
      padding:1rem;
    }
  }
   @media (min-width: 1440px) {
    bottom:68px;
   }


}
.c-hero-slider__title {
  color: $colour-nhs-white;
  font-size: 16px;
  width: 240px;
  margin: 0 auto;
  text-align: center;
  padding-bottom: 0;
  position: absolute;
  bottom: 130px;
  left: 0;
  right: 0;

  @media (min-width: 768px) {
    padding-bottom: 0;
    width: 405px;
  }
  @media (min-width: 1440px) {
    font-size: 24px;
    width: 570px;
    bottom: 142px;
  }
  @media (min-width: 1440px) {
    width: 600px;
  }
  @media (min-width: 1900px) {
    width: 680px;
  }
}
</style>
